import { ChartManager, DataRowExt, PieArcExt } from '../chart.manager';

import {
  D3CircleData,
  D3PathData,
  D3PathFilledData,
  d3renderCircle,
  d3renderGradient,
  d3renderPath,
  d3renderPathFilled,
  d3renderRect,
  d3renderText,
} from '../chart.d3';

import { clamp, cloneObject, roundTo } from '@23degrees/shared-code/utilities';

import chroma from 'chroma-js';

import { interpolate } from 'd3-interpolate';
import { select } from 'd3-selection';
import { arc, DefaultArcObject, line, pie, PieArcDatum } from 'd3-shape';

import { getOldFields } from '@23degrees/shared-code/config/dimension.config';
import { EDITOR_MESSAGES, MESSAGE_RECIPIENTS } from '@config';
import { overlayText } from '../chart.overlay';
import { Vector2 } from '../utilities/vector2';
import { color2css } from '../utilities/utils';

type DataRowPie = PieArcDatum<DataRowExt> & PieArcExt;

const deg2rad = (angle: number) => angle * (Math.PI / 180);

const rad2deg = (radians: number) => radians * (180 / Math.PI);

const midAngle = (obj: DataRowPie) => (obj.endAngle - obj.startAngle) / 2;

const equalizeAngle = (angle: number) => ((angle + Math.PI) % (Math.PI * 2)) - Math.PI;

const angleRange = (angle: number, radius: number) => {
  return [Math.cos(angle) * radius, Math.sin(angle) * radius];
};

const centroidRange = (obj: DataRowPie, radius: number) => {
  const angle = (obj.startAngle + obj.endAngle) / 2 - Math.PI / 2;
  return [Math.cos(angle) * radius, Math.sin(angle) * radius];
};

const closestPointInPieToEquator = (obj: DataRowPie, radius: number) => {
  const piHalf = Math.PI / 2;
  const eq = obj.isRightSide ? Math.PI * 2 : Math.PI;
  const diff = (obj.endAngle - obj.startAngle) / 10;
  const angle = clamp(eq, obj.startAngle - piHalf + diff, obj.endAngle - piHalf - diff);

  return [Math.cos(angle) * radius, Math.sin(angle) * radius];
};

const getAvailableHeight = (obj: DataRowPie, radius: number) => {
  const piHalf = Math.PI / 2;
  const startHeight = Math.sin(obj.startAngle - piHalf) * radius;
  const endHeight = Math.sin(obj.endAngle - piHalf) * radius;
  return Math.abs(endHeight - startHeight);
};

/*

TODOs:
increase/decrease chart height based on donut spacing

*/

// https://github.com/vasturiano/d3-force-registry
// https://observablehq.com/@mootari/d3-path-force
// https://github.com/tinker10/D3-Labeler
// https://bocoup.com/blog/improving-d3-path-animation
// https://github.com/pbeshai/d3-interpolate-path
// https://github.com/atbentley/d3-path-animation
// https://github.com/rveciana/svg-path-properties

// http://bl.ocks.org/dbuezas/9306799
// https://bl.ocks.org/DDDDDanica/661e8bb2ddd79b7bc4cf790ef4f1f648

export const renderDonut = (chartManager: ChartManager): void => {
  // const urlParams = new URLSearchParams(window.location.search);
  // const isApp = chartManager.globalService.isApp();
  // const debugMode = !!urlParams.get('debug') && !isApp;
  const debugMode = false;
  const geoDebug: D3CircleData[] = []; // DEBUG
  const lineDebug: D3PathData[] = []; // DEBUG

  const { typeSpecific } = chartManager.content;
  const { labelField, valueField } = getOldFields(chartManager.content.type, chartManager.content?.typeSpecific?.dimensions);
  const { animation, donut, values } = typeSpecific.chart;
  const { backgroundColor } = typeSpecific.style.background;

  const dynamicStyle = cloneObject(values.style);
  dynamicStyle.size = chartManager.getDynamicFontSize(values.style);

  const startAngle = deg2rad(donut.autoStartAngle ? 360 - donut.degrees * 0.5 : donut.startAngle);
  const endAngle = deg2rad(donut.autoStartAngle ? 360 + donut.degrees * 0.5 : donut.startAngle + donut.degrees);

  const arcFn = arc().cornerRadius(donut.cornerRadius); // .innerRadius(innerRadius).outerRadius(outerRadius).

  const pieFn = pie<DataRowExt>()
    .value((obj) => obj[valueField] as number)
    .sort(null)
    .padAngle(donut.padAngle)
    .startAngle(startAngle)
    .endAngle(endAngle);

  const arcTween = () => {
    return (obj) => {
      // interpolate both its starting and ending angles
      const interpolateStart = interpolate(obj.start.startAngle, obj.end.startAngle);
      const interpolateEnd = interpolate(obj.start.endAngle, obj.end.endAngle);
      return (t) => {
        const options: DefaultArcObject = {
          startAngle: interpolateStart(t),
          endAngle: interpolateEnd(t),
          innerRadius: 0,
          outerRadius: 1,
        };

        return arcFn(options);
      };
    };
  };

  const getClassOutside = (obj: DataRowPie) => {
    const isInside = donut.labelAlignment === 'inside';
    const isOutside = donut.labelAlignment === 'outside';

    return {
      grow: true,
      reverse: donut.labelReverse && !obj.isAbove,
      left: (isInside && obj.isRightSide) || (isOutside && !obj.isRightSide),
      right: (isInside && !obj.isRightSide) || (isOutside && obj.isRightSide),
    };
  };

  const lineFn = line()
    // .curve(curve)
    .x((lineObj) => roundTo(lineObj[0])) //  + offset[0]
    .y((lineObj) => roundTo(lineObj[1])); //  + offset[1]

  const isPointInCircle = (x: number, y: number, centerX: number, centerY: number, radius: number) => {
    const distSquare = (x - centerX) ** 2 + (y - centerY) ** 2;
    return distSquare < radius ** 2;
  };

  const positiveData = chartManager.data.filter((obj) => (obj[valueField] as number) > 0);
  const pieData = pieFn(positiveData) as (DataRowPie & D3PathFilledData)[];

  let maxLabelWidth = 0;
  let maxLabelHeight = 0;
  let maxLabelWidthLeft = 0;
  let maxLabelWidthRight = 0;

  for (const obj of pieData) {
    obj.midAngle = obj.startAngle + midAngle(obj);

    obj.isAbove = Math.cos(obj.midAngle) >= 0;
    obj.isRightSide = Math.sin(obj.midAngle) >= 0;

    obj.isStartAbove = Math.cos(obj.startAngle) >= 0;
    obj.isStartRightSide = Math.sin(obj.startAngle) >= 0;

    obj.isEndAbove = Math.cos(obj.endAngle) >= 0;
    obj.isEndRightSide = Math.sin(obj.endAngle) >= 0;

    obj.isStartInDifferentQuadrant = obj.isStartAbove !== obj.isAbove || obj.isStartRightSide !== obj.isRightSide;
    obj.isEndInDifferentQuadrant = obj.isEndAbove !== obj.isAbove || obj.isEndRightSide !== obj.isRightSide;

    obj.classOutside = getClassOutside(obj);

    const { dataLabelDonutStr } = obj.data['~meta'];
    const { width: minWidth, height: maxHeight } = chartManager.getElementRect(dataLabelDonutStr, 0, undefined, true, undefined, false, true);

    obj.minWidth = minWidth;
    obj.maxHeight = maxHeight;

    maxLabelWidth = Math.max(maxLabelWidth, minWidth);
    maxLabelHeight = Math.max(maxLabelHeight, maxHeight);

    if (obj.isRightSide) {
      maxLabelWidthRight = Math.max(maxLabelWidthRight, minWidth);
    } else {
      maxLabelWidthLeft = Math.max(maxLabelWidthLeft, minWidth);
    }

    // console.log(obj.isRightSide ? 'right' : 'left', minWidth);
  }

  // console.log('max label width/height', maxLabelWidth, maxLabelHeight);

  maxLabelWidth = Math.min(maxLabelWidth, chartManager.chartWidth / 4);
  maxLabelWidthLeft = Math.min(maxLabelWidthLeft, chartManager.chartWidth / 4);
  maxLabelWidthRight = Math.min(maxLabelWidthRight, chartManager.chartWidth / 4);

  const centerX = chartManager.chartWidth / 2;

  const autoHeight = 0.5 + Math.sin(deg2rad(donut.degrees / 2 - 90)) / 2;
  const manualHeight = 1;

  const heightFactor = Math.max(0.5, donut.autoStartAngle ? autoHeight : manualHeight);
  let radius = Math.min(chartManager.chartWidth, chartManager.chartHeight / heightFactor) / 2;

  if (values.enabled && donut.labelsOutside) {
    const lineDistance = donut.labelDistance * 2;
    radius = Math.min(radius, centerX - maxLabelWidth - lineDistance);

    // if (donut.labelResizeDonut) {
    //   radius = Math.min(radius, chartManager.chartHeight / 2 - maxLabelHeight);
    // }
  }

  const innerRadius: number = radius * donut.innerRadius;
  const outerRadius: number = radius * donut.outerRadius;
  const textRadius: number = radius * (donut.innerRadius + (donut.outerRadius - donut.innerRadius) / 2);

  const maxHeightTop = radius;
  const maxHeightBottom = radius;

  const centerY = chartManager.chartHeight / 2 + radius - radius * heightFactor;
  // console.log('chart width/height', chartManager.chartWidth, chartManager.chartHeight, 'radius', radius);

  // Debug Axis
  /*
  lineDebug.push({
    color: '#51f7f2',
    path: lineFn([
      [centerX, 0],
      [centerX, chartManager.chartHeight],
    ]),
    width: 1,
  });
  lineDebug.push({
    color: '#51f7f2',
    path: lineFn([
      [0, centerY],
      [chartManager.chartWidth, centerY],
    ]),
    width: 1,
  });
  */

  // Debug Bounds
  /*
  lineDebug.push({
    color: '#51f7f2',
    path: lineFn([
      [centerX + outerRadius, 0],
      [centerX + outerRadius, chartManager.chartHeight],
    ]),
    width: 1,
  });
  lineDebug.push({
    color: '#51f7f2',
    path: lineFn([
      [centerX - outerRadius, 0],
      [centerX - outerRadius, chartManager.chartHeight],
    ]),
    width: 1,
  });
  */

  const vecCenter = new Vector2(centerX, centerY);

  // console.log('full', roundTo(chartManager.chartWidth), 'radius', roundTo(radius), 'left', roundTo(maxLabelWidthLeft), 'right', roundTo(maxLabelWidthRight));

  const pathData: D3PathData[] = [];
  const circleData: D3CircleData[] = [];

  const overlayData = [];

  const moveAtoB = <T extends DataRowPie>(arrayFrom: T[], arrayTo: T[], options = { reverse: false, isStart: true }) => {
    if (arrayFrom.length > arrayTo.length + 1) {
      const idx = options.reverse ? arrayFrom.length - 1 : 0;
      const key = options.isStart ? 'isStartInDifferentQuadrant' : 'isEndInDifferentQuadrant';

      if (arrayFrom[idx][key]) {
        const obj = options.reverse ? arrayFrom.pop() : arrayFrom.shift();

        if (options.reverse) {
          obj.isRightSide = !obj.isRightSide;
        } else {
          obj.isAbove = !obj.isAbove;
        }

        obj.classOutside = getClassOutside(obj);
        options.reverse ? arrayTo.push(obj) : arrayTo.unshift(obj);
      }
    }
  };

  if (values.enabled && donut.labelsOutside) {
    const twoPi = Math.PI * 2;
    pieData.sort((a, b) => (a.midAngle % twoPi) - (b.midAngle % twoPi));

    const dataLeftBottom = pieData.filter((obj) => !obj.isRightSide && !obj.isAbove).reverse();
    const dataLeftTop = pieData.filter((obj) => !obj.isRightSide && obj.isAbove);
    const dataRightTop = pieData.filter((obj) => obj.isRightSide && obj.isAbove).reverse();
    const dataRightBottom = pieData.filter((obj) => obj.isRightSide && !obj.isAbove);

    moveAtoB(dataLeftBottom, dataRightBottom, { reverse: true, isStart: true });
    moveAtoB(dataLeftBottom, dataLeftTop, { reverse: false, isStart: false });
    moveAtoB(dataLeftTop, dataLeftBottom, { reverse: false, isStart: true });
    moveAtoB(dataLeftTop, dataRightTop, { reverse: true, isStart: false });
    moveAtoB(dataRightTop, dataLeftTop, { reverse: true, isStart: true });
    moveAtoB(dataRightTop, dataRightBottom, { reverse: false, isStart: false });
    moveAtoB(dataRightBottom, dataRightTop, { reverse: false, isStart: true });
    moveAtoB(dataRightBottom, dataLeftBottom, { reverse: true, isStart: false });

    const getAnchorOffset = (obj: DataRowPie) => {
      const offsetX = obj.isRightSide ? 0 : -obj.widthOutside;
      const offsetY = obj.isAbove ? -obj.heightOutside : 0;
      return [offsetX, offsetY];
    };

    const getCenterAngleDiff = (obj: DataRowPie) => {
      // TODO Saulo
    };

    const getAngleByHeight = (obj: DataRowPie, height: number, reference: number = outerRadius) => {
      const offset = obj.isRightSide ? 0 : Math.PI;
      let angle = Math.asin(clamp(height / reference, 0, 1));
      if ((obj.isRightSide && obj.isAbove) || (!obj.isRightSide && !obj.isAbove)) {
        angle *= -1;
      }

      return angle + offset;
    };

    const getPositionByAngle = (angle: number, radius: number) => {
      return [Math.cos(angle) * radius, Math.sin(angle) * radius];
    };

    const getBoxOffset = (boxHeight: number, theta: number, radius: number) => {
      // def placeBox(h, theta, r):
      //   """ h is the height of the box. Theta is the angle across the pie chunk and r is the original radius"""
      //   a= 1
      //   b= -h* math.sin(theta)
      //   c= (h**2)/4 - (r +10)**2
      //   R= (-b + sqrt(b**2 -4*a*c))/2
      //   return R
      const a = 1;
      const b = -boxHeight * Math.sin(theta);
      const c = boxHeight ** 2 / 4 - (radius + 10) ** 2;
      const R = (-b + Math.sqrt(b ** 2 - 4 * a * c)) / 2;
      return R;
    };

    const positionLabel = (data: DataRowPie[]) => {
      let prevHeight = 0;
      let usedHeight = 0;
      for (let idx = 0; idx < data.length; idx++) {
        const obj = data[idx];
        const logLabel = `[${obj.data[labelField]}, ${obj.data[valueField]}]`;
        const availableWidth = obj.isRightSide ? maxLabelWidthRight : maxLabelWidthLeft;
        // const heightFraction = Math.max(0, (centerY - cursorHeight) / centerY);
        // const availableWidth = centerX - outerRadius * Math.sin((heightFraction * Math.PI) / 2);

        const nextHeightToConsider = data.reduce((acc, obj, curIdx) => (curIdx <= idx ? acc : acc + obj.maxHeight), 0);

        const { dataLabelDonutStr } = obj.data['~meta'];

        const { width, height } = chartManager.getElementRect(dataLabelDonutStr, availableWidth, undefined, true, undefined, false, false);

        obj.widthOutside = availableWidth;
        obj.heightOutside = Math.max(obj.maxHeight, height);

        let xOffset = 0;
        let yOffset = 0;

        // try to improve this position along the pie slice towards the donut center line
        const dockingPos = centroidRange(obj, outerRadius + donut.labelDistance);
        let pos = getPositionByAngle(obj.midAngle - Math.PI / 2, outerRadius + donut.labelDistance);

        let freeSpaceWidth = obj.widthOutside;
        let labelXOffset = 0;

        const availableSpace = obj.isAbove ? centerY : chartManager.chartHeight - centerY;

        if (Math.abs(pos[1]) < prevHeight) {
          // TODO properly move x/y outwards
          pos[1] = prevHeight * (obj.isAbove ? -1 : 1);
        }

        const isOutsideAbove = pos[1] - obj.heightOutside + centerY <= 0;
        const isOutsideBelow = pos[1] + obj.heightOutside + centerY > chartManager.chartHeight;

        if (isOutsideAbove || isOutsideBelow) {
          // This causes overlapping problems
          const relativeHeight = availableSpace - obj.heightOutside;
          const maxAngle = getAngleByHeight(obj, relativeHeight, outerRadius + donut.labelDistance);
          pos = getPositionByAngle(maxAngle, outerRadius + donut.labelDistance);
        }

        let currentHeight = Math.abs(pos[1]) + obj.heightOutside;
        const availableHeight = Math.abs(pos[1]) - usedHeight;
        if (nextHeightToConsider > 0 && currentHeight > centerY - nextHeightToConsider) {
          const maxMoveHeight = Math.min(availableHeight, Math.abs(centerY - currentHeight - nextHeightToConsider));

          const relativeHeight = Math.abs(pos[1]) - maxMoveHeight;
          const maxAngle = getAngleByHeight(obj, relativeHeight, outerRadius + donut.labelDistance);
          pos = getPositionByAngle(maxAngle, outerRadius + donut.labelDistance);
          currentHeight = Math.abs(pos[1]) + obj.heightOutside;
        }

        let realOutsideWidth = 0;

        // Make the labels get the most space
        if (obj.isRightSide) freeSpaceWidth = chartManager.chartWidth - (pos[0] + centerX);
        else freeSpaceWidth = pos[0] + centerX;

        if (donut.labelAlignSides) {
          freeSpaceWidth = centerX - outerRadius - donut.labelDistance;
          if (obj.isRightSide) pos[0] = outerRadius + donut.labelDistance;
          else pos[0] = -(outerRadius + donut.labelDistance);
        }

        const _class = getClassOutside(obj);
        const extraClasses: string[] = [];
        for (const [className, enabled] of Object.entries<boolean>(_class)) if (enabled) extraClasses.push(className);

        // const { width: realWidth } = chartManager.getElementRect(dataLabelDonutStr, freeSpaceWidth, undefined, true, undefined, false, true, extraClasses, true);
        const { height: realHeight, width: realWidth } = chartManager.getElementRect(
          dataLabelDonutStr,
          freeSpaceWidth,
          undefined,
          false,
          undefined,
          false,
          false,
          extraClasses,
        );

        // console.log("[w, h]", [realWidth, realHeight])
        obj.widthOutside = freeSpaceWidth; //realWidth
        obj.heightOutside = realHeight;
        realOutsideWidth = realWidth;
        if (donut.labelAlignment === 'inside') labelXOffset = freeSpaceWidth - realWidth;
        if (donut.labelAlignment === 'default') labelXOffset = (freeSpaceWidth - realWidth) / 2;
        // if(donut.labelAlignment === 'outside') labelXOffset = freeSpaceWidth - realWidth;

        if (donut.labelAttachment === 'default' || donut.lineStyle === 'direct') {
          xOffset = obj.isRightSide ? 0 : obj.widthOutside;
          yOffset = obj.heightOutside / 2;
        } else if (donut.labelAttachment === 'top') {
          xOffset = obj.isRightSide ? obj.widthOutside : 0;
          yOffset = obj.isAbove || !donut.labelReverse ? 0 : obj.heightOutside;
        } else if (donut.labelAttachment === 'bottom') {
          xOffset = obj.isRightSide ? obj.widthOutside : 0;
          yOffset = obj.isAbove || !donut.labelReverse ? obj.heightOutside : 0;
        }

        const offset = getAnchorOffset(obj);

        // geoDebug.push({
        //   x: pos[0] + centerX,
        //   y: pos[1] + centerY,
        //   color: '#ea12e3',
        //   radius: 3,
        //   stroke: '#ea12e3',
        //   strokeWidth: 0,
        // });

        const labelX = pos[0] + offset[0] + centerX;
        const labelY = pos[1] + offset[1] + centerY;

        prevHeight = currentHeight;
        usedHeight = Math.abs(pos[1]) + obj.heightOutside;

        // lines always have the same start position
        const startRadius = innerRadius + (outerRadius - innerRadius) * donut.lineStart;
        const startPos = centroidRange(obj, startRadius);
        // if (donut.labelPreferCenter) {
        //   startPos = closestPointInPieToEquator(obj, startRadius);
        // }

        startPos[0] += centerX;
        startPos[1] += centerY;

        const labelData = {
          html: dataLabelDonutStr,
          x: labelX,
          y: labelY,
          width: obj.widthOutside,
          height: obj.heightOutside,
          class: obj.classOutside,
          // color: color2css(textColor),
          // callbacks,
          justifyContent: 'center',
          alignItems: 'center',
          opacity: 0,
          transform: `translate(${roundTo(labelX)}px, ${roundTo(labelY)}px)`,
          animate: {
            enabled: animation.enabled,
            delay: animation.delay,
            duration: animation.duration,
            opacity: 1,
            transform: `translate(${roundTo(labelX)}px, ${roundTo(labelY)}px)`,
          },
        };

        // DEBUG
        // if (debugMode) (labelData as any).outline = '1px solid #c77ec4';

        overlayData.push(labelData);

        const pathArray = [];

        const yDiff = labelY + yOffset - startPos[1];
        const vertLineHeight = getAvailableHeight(obj, startRadius);
        // console.log(logLabel, 'height', vertLineHeight);
        // if (Math.abs(yDiff) < 20) {
        //   if (yDiff >= 0) {
        //     startPos[1] -= 20 - Math.abs(yDiff);
        //   } else {
        //     startPos[1] += 20 - Math.abs(yDiff);
        //   }
        // }

        pathArray.push(startPos);

        dockingPos[0] += centerX;
        dockingPos[1] += centerY;

        const xDistOffset = (obj.isRightSide ? -donut.labelDistance : donut.labelDistance) / 2;
        const xAngleOffset = (labelY + yOffset - centerY) / Math.tan(Math.PI / 2 - obj.midAngle);

        if (donut.lineStyle === 'default') {
          const xPos = obj.isRightSide ? labelX + xDistOffset : labelX + obj.widthOutside + xDistOffset; // Math.cos(offsetAngle) * (outerRadius + xDistOffset) + centerX; //
          const yPos = labelY + yOffset; // obj.isAbove ? Math.max(dockingPos[1], labelY + yOffset) : Math.min(dockingPos[1], labelY + yOffset);
          pathArray.push([xPos, yPos]);
          if (obj.isRightSide) {
            if (donut.labelAttachment === 'default') pathArray.push([chartManager.chartWidth - labelXOffset - realOutsideWidth, labelY + yOffset]);
            else pathArray.push([chartManager.chartWidth - labelXOffset, labelY + yOffset]);
          } else {
            if (donut.labelAttachment === 'default') pathArray.push([realOutsideWidth + labelXOffset, labelY + yOffset]);
            else pathArray.push([labelX + xOffset + labelXOffset, labelY + yOffset]);
          }
        } else if (donut.lineStyle === 'direct') {
          if (obj.isRightSide) {
            pathArray.push([chartManager.chartWidth - labelXOffset - realOutsideWidth, labelY + yOffset]);
          } else {
            pathArray.push([labelXOffset + realOutsideWidth, labelY + yOffset]);
          }
        } else if (donut.lineStyle === '90deg') {
          pathArray.push([startPos[0], labelY + yOffset]);
          if (obj.isRightSide) {
            if (donut.labelAttachment === 'default') pathArray.push([chartManager.chartWidth - labelXOffset - realOutsideWidth, labelY + yOffset]);
            else pathArray.push([chartManager.chartWidth - labelXOffset, labelY + yOffset]);
          } else {
            if (donut.labelAttachment === 'default') pathArray.push([realOutsideWidth + labelXOffset, labelY + yOffset]);
            else pathArray.push([labelX + xOffset + labelXOffset, labelY + yOffset]);
          }
        }
        /*
        // Original Code:
        if (donut.lineStyle === 'default') {
          // const xPos = obj.isRightSide
          //   ? Math.min(labelX + xDistOffset, centerX - xAngleOffset)
          //   : Math.max(labelX + obj.widthOutside + xDistOffset, centerX - xAngleOffset);
          // const yPos = obj.isAbove ? Math.min(dockingPos[1], labelY + yOffset) : Math.max(dockingPos[1], labelY + yOffset);
          const xPos = obj.isRightSide ? labelX + xDistOffset : labelX + obj.widthOutside + xDistOffset; // Math.cos(offsetAngle) * (outerRadius + xDistOffset) + centerX; //
          const yPos = labelY + yOffset; // obj.isAbove ? Math.max(dockingPos[1], labelY + yOffset) : Math.min(dockingPos[1], labelY + yOffset);

          pathArray.push([xPos, yPos]);
          pathArray.push([labelX + xOffset, labelY + yOffset]);
        } else if (donut.lineStyle === 'direct') {
          // pathArray.push(dockingPos);
          pathArray.push([labelX + xOffset, labelY + yOffset]);
        } else if (donut.lineStyle === '90deg') {
          pathArray.push([startPos[0], labelY + yOffset]);
          pathArray.push([labelX + xOffset, labelY + yOffset]);
        }
        */
        // console.log("pathArray", pathArray)

        // anchorPos[0] += isRightSide ? 0 : obj.width ?? obj.widthOutside;
        if (donut.labelAttachment === 'default') {
          // anchorPos[1] += (obj.height ?? obj.heightOutside) / 2;
        } else if (donut.labelAttachment === 'top') {
          // anchorPos[1] += isAbove || !donut.labelReverse ? 0 : obj.height ?? obj.heightOutside;
        } else if (donut.labelAttachment === 'bottom') {
          // anchorPos[1] += isAbove || !donut.labelReverse ? obj.height ?? obj.heightOutside : 0;
        }

        if (['top', 'bottom'].includes(donut.labelAttachment)) {
          // const linePos = [anchorPos[0], anchorPos[1]];
          // const isLineAbove = anchorPos[1] - startPos[1] >= 0;
          // const isLineRightSide = anchorPos[0] - startPos[0] >= 0;
          // if (isRightSide) {
          //   linePos[0] += obj.width ?? obj.widthOutside;
          // } else {
          //   linePos[0] -= obj.width ?? obj.widthOutside;
          // }
          // pathArray.push(linePos);
        }

        const boxLine = lineFn([
          [labelX, labelY],
          [labelX + labelData.width, labelY],
          [labelX + labelData.width, labelY + labelData.height],
          [labelX, labelY + labelData.height],
          [labelX, labelY],
        ]);

        const labelLine = lineFn(pathArray);

        pathData.push({
          color: donut.lineColor,
          path: labelLine,
          width: donut.lineWidth,
          animate: {
            enabled: animation.enabled,
            delay: animation.delay,
            duration: animation.duration,
            // path,
          },
        });

        // pathData.push({
        //   color: '#ff0000',
        //   path: boxLine,
        //   width: 1,
        //   animate: {
        //     enabled: animation.enabled,
        //     delay: animation.delay,
        //     duration: animation.duration,
        //     // path,
        //   },
        // });

        circleData.push({
          x: startPos[0],
          y: startPos[1],
          color: donut.lineDotColor,
          radius: donut.lineDotSize,
          stroke: donut.lineDotOutlineColor,
          strokeWidth: donut.lineDotOutlineWidth,
        });
      }
    };

    // Render 4 quadrants
    // console.log({ leftTop: dataLeftTop.length, leftBottom: dataLeftBottom.length, rightTop: dataRightTop.length, rightBottom: dataRightBottom.length });
    // console.log('>> left bottom');
    positionLabel(dataLeftBottom);
    // console.log('>> left top');
    positionLabel(dataLeftTop);
    // console.log('>> right top');
    positionLabel(dataRightTop);
    // console.log('>> right bottom');
    positionLabel(dataRightBottom);
  }

  const { mode } = chartManager.globalService.getParameters();
  if (typeSpecific.chart.optimalHeight && mode !== 'export') {
    const deltaHeight = roundTo(Math.max(maxHeightTop, maxHeightBottom) * 2 - chartManager.chartHeight);

    if (Math.abs(deltaHeight) > 1) {
      chartManager.messageBus.emit(EDITOR_MESSAGES.DisplayUpdate, [MESSAGE_RECIPIENTS.Editor], { delta: { height: deltaHeight } });

      try {
        const { blockPostMessages } = chartManager.content.localStorage;
        if (!blockPostMessages && window?.parent) {
          const location = chartManager.document.location.href;
          const clientHeight = chartManager.document.children[0].clientHeight;
          const dynamicHeight = clientHeight + deltaHeight;

          if (Number.isFinite(dynamicHeight)) {
            chartManager.trackerService.postMessage({
              action: 'dynamicSize',
              height: dynamicHeight,
              contentSlug: chartManager.content.slug,
              location: location,
            });

            chartManager.trackerService.postMessage({ sentinel: 'amp', type: 'embed-size', height: dynamicHeight });
          }
        }
      } catch (error) {
        console.log('[DonutChart] postMessage error:', error);
      }
    }
  }

  for (const obj of pieData) {
    const label = obj.data[labelField];
    const value = obj.data[valueField];

    const color = chartManager.colorScale(obj.data);
    const hoverColor = color2css(chroma(color).darken());
    const isContrastLight = chroma(color).hsl()[2] > values.lightnessThreshold; // donut.labelsOutside ? backgroundColor :
    const textColor = isContrastLight ? values.darkColor : values.lightColor;

    const click = (event) => {
      chartManager.openPopup(event, obj.data);
      chartManager.mouseClick(event, obj.data);
    };

    const enter = (event, _, node) => {
      select(node).attr('fill', hoverColor);
      chartManager.mouseEnter(event, obj.data);
    };

    const leave = (event, _, node) => {
      select(node).attr('fill', color);
      chartManager.mouseLeave(event, obj.data);
    };

    const move = (event, _, node) => chartManager.mouseMove(event, obj.data);

    const callbacks = { click, enter, leave, move };

    obj.innerRadius = innerRadius;
    obj.outerRadius = outerRadius;

    const animate = {
      enabled: animation.enabled,
      delay: animation.delay,
      duration: animation.duration,
      color: color2css(color),
      // path: arcFn(obj),
    };

    const pathFn = (node: { prev: typeof obj }) => {
      const intFn = interpolate(node.prev, obj);
      node.prev = intFn(0);
      return (time: number) => arcFn(intFn(time));
    };

    const prev = Object.assign({}, obj);
    prev.endAngle = obj.startAngle;

    obj.path = arcFn(prev);
    obj.prev = prev;
    obj.pathFn = pathFn;
    obj.color = color;
    obj.transform = `translate(${centerX}, ${centerY})`;
    obj.animate = animate;
    obj.callbacks = callbacks;

    if (!values.enabled) continue;
    const { dataLabelStr, dataLabelWidth, dataLabelHeight, dataLabelDonutStr, dataLabelDonutWidth, dataLabelDonutHeight } = obj.data['~meta'];
    const absAngle = Math.abs(obj.startAngle - obj.endAngle);
    const availableSpace = absAngle * textRadius;

    obj.widthInside = dataLabelWidth;
    obj.heightInside = dataLabelHeight;

    obj.widthOutside = dataLabelDonutWidth;
    obj.heightOutside = dataLabelDonutHeight;

    if (donut.labelsOutside) {
    }

    obj.textInside = arcFn.centroid(obj);
    obj.textOutside = centroidRange(obj, outerRadius);

    // apply offset
    obj.textInside[0] += centerX;
    obj.textInside[1] += centerY;

    obj.textOutside[0] += centerX;
    obj.textOutside[1] += centerY;

    // shift start position to be centered
    obj.textInside[0] -= obj.widthInside / 2;
    obj.textInside[1] -= obj.heightInside / 2;

    // const midAng = obj.startAngle + midAngle(obj);
    // const midCos = Math.cos(midAng);
    // const midSin = Math.sin(midAng);

    // const isAbove = midCos >= 0;
    // const isRightSide = midSin >= 0;

    // obj.isAbove = isAbove;
    // obj.isRightSide = isRightSide;

    // obj.textOutside[0] += isRightSide ? obj.widthOutside / 2 : -obj.widthOutside / 2;
    // obj.textOutside[1] += isAbove ? -obj.heightOutside / 2 : obj.heightOutside / 2;
    // if (donut.lineStyle === '90deg') obj.textOutside[1] += isAbove ? -donut.lineHeight : donut.lineHeight;

    if (donut.labelsInside && (availableSpace >= dataLabelWidth || values.forceDraw)) {
      const updatedStyles = [];
      const contrastPattern = isContrastLight ? /dark-(color:(?:#[0-9a-f]+|rgba?\([^\)]+\)))/ : /light-(color:(?:#[0-9a-f]+|rgba?\([^\)]+\)))/;
      const contrastMatch = dataLabelStr.match(contrastPattern);
      if (contrastMatch?.[1]) {
        updatedStyles.push(contrastMatch[1]);
      }

      const backgroundPattern = /(background-color:(?:#[0-9a-f]+|rgba?\([^\)]+\)))/;
      const backgroundMatch = dataLabelStr.match(backgroundPattern);
      if (backgroundMatch?.[1]) {
        updatedStyles.push(backgroundMatch[1]);
      }

      let idx = 0;
      const replaceFn = (match, ...args) => {
        if (match.includes('background') && backgroundMatch && backgroundMatch[1]) return `${backgroundMatch[1]};`;
        if (contrastMatch && idx < contrastMatch.length) return `${contrastMatch[idx++].replace(/(dark-|light-)/g, '')};`;

        return '';
      };

      // (?<!background-)
      const noColorStr = dataLabelStr.replace(/([";]color:(?:#[0-9a-f]+|rgba?\([^\)]+\));?)+/g, '');
      const fixedColorStr = noColorStr.replace(/((?:dark-|light-|background-)?color:(?:#[0-9a-f]+|rgba?\([^\)]+\));?)+/g, replaceFn); // updatedStyles.join(';') + ';'

      const labelData = {
        html: fixedColorStr,
        x: obj.textInside[0],
        y: obj.textInside[1],
        width: obj.widthInside,
        height: obj.heightInside,
        // class: obj.class,
        color: color2css(textColor),
        callbacks,
        justifyContent: 'center',
        alignItems: 'center',
        transform: `translate(${roundTo(obj.textInside[0])}px, ${roundTo(obj.textInside[1])}px)`,
        animate: {
          enabled: animation.enabled,
          delay: animation.delay,
          duration: animation.duration,
          transform: `translate(${roundTo(obj.textInside[0])}px, ${roundTo(obj.textInside[1])}px)`,
        },
      };

      overlayData.push(labelData);
    }

    if (donut.labelsOutside) {
    }
  }

  // clear
  d3renderRect(chartManager.chart.element, [], '.chart');
  d3renderPath(chartManager.chart.element, [], '.chart');
  d3renderCircle(chartManager.chart.element, [], '.chart');
  d3renderGradient(chartManager.chart.element, [], '.chart');
  d3renderPathFilled(chartManager.chart.element, [], '.chart');
  d3renderPathFilled(chartManager.chart.element, [], '.voronoi');
  d3renderText(chartManager.chart.element, [], '.text');

  // Debug
  if (debugMode) {
    for (const pie of pieData) {
      pie.debug = true;
      pie.stroke = '#ddd';
      pie.strokeWidth = 1;
    }
  }

  d3renderPathFilled(chartManager.chart.element, pieData, '.chart', chartManager.animationStop);
  // d3renderText(chartManager.chart, textData, '.text');

  if (debugMode) {
    d3renderPath(chartManager.chart.element, [...pathData, ...lineDebug], '.grid-foreground');
    d3renderCircle(chartManager.chart.element, [...circleData, ...geoDebug], '.grid-foreground');
  } else {
    d3renderPath(chartManager.chart.element, pathData, '.grid-foreground');
    d3renderCircle(chartManager.chart.element, circleData, '.grid-foreground');
  }

  overlayText(chartManager.overlay, overlayData, '.overlay-values');
};
